/**
 * Subscription plans. Must match the backend (accounts.py / billing.py).
 */

import type { PaidPlan, Plan } from './accountApi';

export interface PlanDetails {
  id: PaidPlan;
  name: string;
  monthlyPrice: number;
  tagline: string;
  intro?: string;
  features: string[];
}

export const PLANS: PlanDetails[] = [
  {
    id: 'standard',
    name: 'Standard',
    monthlyPrice: 15,
    tagline: 'Sharpen the core rounds every engineering interview has.',
    features: [
      'Live Coding with a real editor, JS runner and React sandbox',
      'Technical Q&A interviews',
      'Behavioural interviews with STAR coaching',
      'Talk it through with the AI voice interviewer',
      'Scored feedback after every interview',
      'Full interview history',
      'Company-style templates (FAANG, startup and more)',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 30,
    tagline: 'The complete loop, tailored to the role you want.',
    intro: 'Everything in Standard, plus:',
    features: [
      'System Design with whiteboard and screen analysis',
      'Architecture Review of your own diagrams',
      'CV Review interviews based on your CV',
      'Focus interviews built from a real job description',
      'Case Study Prep',
      'Multi-stage interview sessions',
      'Camera and audio recordings of your interviews',
      'Google Calendar scheduling',
      'Transcript export',
    ],
  },
];

export const STANDARD_INTERVIEW_TYPES = ['coding', 'technical', 'behavioural'];

export const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  system_design: 'System Design',
  coding: 'Live Coding',
  technical: 'Technical Q&A',
  architecture_review: 'Architecture Review',
  behavioural: 'Behavioural Interview',
  cv_review: 'CV Review',
  focus_interview: 'Focus Interview (Job-Specific)',
  case_study: 'Case Study Prep',
};

export const getPlanDetails = (plan: PaidPlan): PlanDetails =>
  PLANS.find((p) => p.id === plan) as PlanDetails;

interface PlanHolder {
  accountType?: 'legacy' | 'account';
  account?: { plan: Plan } | null;
}

/** Team logins (accountType legacy or unset) have Pro access. */
export const getUserPlan = (user: PlanHolder | null | undefined): Plan => {
  if (!user) return 'none';
  if (user.accountType !== 'account') return 'pro';
  return user.account?.plan ?? 'none';
};

export const canUseInterviewType = (plan: Plan, type: string): boolean =>
  plan === 'pro' || (plan === 'standard' && STANDARD_INTERVIEW_TYPES.includes(type));

export const hasProFeatures = (plan: Plan): boolean => plan === 'pro';

export const formatPrice = (amount: number): string => `£${amount}`;

export const formatDate = (unixSeconds: number | null | undefined): string =>
  unixSeconds
    ? new Date(unixSeconds * 1000).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';
